import React from 'react';
import { Button, Input, Tag, Tooltip, Typography } from 'antd';
import type { TableProps } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';

const { Text } = Typography;

/** Mirrors backend LINE_SPECIFICATIONS_MAX_LENGTH (procurement line-specifications.util). */
export const LINE_SPECIFICATIONS_MAX_LENGTH = 4000;

function clean(value: string | null | undefined): string {
  return String(value ?? '').trim();
}

/** Description cell text with a trailing `Spec:` paragraph (browser print layouts). */
export function withSpecificationLine(description: string, spec?: string | null): string {
  const text = clean(spec);
  return text ? `${description}\nSpec: ${text}` : description;
}

/** Read-only spec paragraph shown under a line description; renders nothing when blank. */
export const LineSpecificationsText: React.FC<{
  value?: string | null;
  label?: string;
  /** Highlight as a supplier deviation (offered spec differs from required). */
  deviation?: boolean;
  style?: React.CSSProperties;
}> = ({ value, label = 'Spec', deviation, style }) => {
  const text = clean(value);
  if (!text) return null;
  return (
    <div style={{ marginTop: 2, ...style }}>
      <Text
        type={deviation ? 'warning' : 'secondary'}
        style={{ fontSize: 12, whiteSpace: 'pre-wrap', display: 'block' }}
      >
        <Text strong type={deviation ? 'warning' : 'secondary'} style={{ fontSize: 12 }}>
          {label}:
        </Text>{' '}
        {text}
      </Text>
    </div>
  );
};

/** Plain multi-line spec editor (expanded row body). */
export const LineSpecificationsEditor: React.FC<{
  value?: string | null;
  onChange?: (value: string) => void;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
}> = ({ value, onChange, disabled, label = 'Technical specification', placeholder }) => (
  <div style={{ padding: '4px 8px' }}>
    <Text strong style={{ fontSize: 12 }}>
      {label}
    </Text>
    <Input.TextArea
      value={value ?? ''}
      onChange={(e) => onChange?.(e.target.value)}
      disabled={disabled}
      autoSize={{ minRows: 2, maxRows: 10 }}
      maxLength={LINE_SPECIFICATIONS_MAX_LENGTH}
      showCount
      placeholder={placeholder ?? 'Grade, standard, size, finish, tolerances, certificates required…'}
      style={{ marginTop: 4 }}
    />
  </div>
);

/** True when the offered spec is present and differs from the required spec (whitespace-insensitive). */
export function isSpecDeviation(required?: string | null, offered?: string | null): boolean {
  const o = clean(offered).replace(/\s+/g, ' ').toLowerCase();
  if (!o) return false;
  return o !== clean(required).replace(/\s+/g, ' ').toLowerCase();
}

/**
 * antd Table `expandable` config: a "Spec" toggle per line that opens the spec editor
 * (or the read-only text when `onChange` is omitted).
 */
export function lineSpecificationsExpandable<T extends object>(opts: {
  getValue: (row: T) => string | null | undefined;
  onChange?: (row: T, value: string) => void;
  disabled?: boolean;
  label?: string;
}): NonNullable<TableProps<T>['expandable']> {
  const readOnly = !opts.onChange;
  return {
    columnWidth: 64,
    rowExpandable: (row) => !readOnly || Boolean(clean(opts.getValue(row))),
    expandedRowRender: (row) =>
      readOnly ? (
        <LineSpecificationsText value={opts.getValue(row)} label={opts.label ?? 'Spec'} />
      ) : (
        <LineSpecificationsEditor
          value={opts.getValue(row)}
          onChange={(v) => opts.onChange?.(row, v)}
          disabled={opts.disabled}
          label={opts.label}
        />
      ),
    expandIcon: ({ expanded, onExpand, record, expandable }) => {
      if (!expandable) return null;
      const filled = Boolean(clean(opts.getValue(record)));
      return (
        <Tooltip title={expanded ? 'Hide specification' : filled ? 'View / edit specification' : 'Add specification'}>
          <Button
            size="small"
            type={expanded ? 'primary' : 'text'}
            icon={<FileTextOutlined />}
            onClick={(e) => onExpand(record, e)}
            style={{ paddingInline: 4 }}
          >
            {filled ? (
              <Tag color="blue" style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: '14px', paddingInline: 3 }}>
                Spec
              </Tag>
            ) : null}
          </Button>
        </Tooltip>
      );
    },
  };
}
